/**
 * ArtifactTabs
 * 
 * Tab bar for switching between open artifacts
 */

import React from 'react';
import { useWorkspace } from '../artifacts';
import type { Artifact, ArtifactType } from '../artifacts';

const typeIcons: Record<ArtifactType, React.ReactNode> = {
  document: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  email: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  report: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  data: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
  ),
};

interface TabProps {
  artifact: Artifact;
  isActive: boolean;
  onSelect: () => void;
  onClose: () => void;
}

const Tab: React.FC<TabProps> = ({ artifact, isActive, onSelect, onClose }) => {
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`
        group flex items-center gap-2 px-3 py-1.5 text-xs font-medium border-b-2 transition-all cursor-pointer select-none
        ${isActive 
          ? 'border-blue-500 text-blue-700 bg-blue-50/50' 
          : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
        }
        focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-0
      `}
      aria-pressed={isActive}
      aria-label={`Open ${artifact.title}`}
    >
      <span className={isActive ? 'text-blue-600' : 'text-neutral-400'}>
        {typeIcons[artifact.type]}
      </span>
      <span className="max-w-[120px] truncate">{artifact.title}</span>
      {artifact.status === 'draft' && (
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Draft" />
      )}
      {artifact.access_level && artifact.access_level !== 'owner' && (
        <svg
          className="w-3 h-3 text-neutral-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          role="img"
        >
          <title>{artifact.access_level === 'edit' ? 'Shared (can edit)' : 'Shared (view only)'}</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
      )}
      <button
        type="button"
        onClick={handleClose}
        className="ml-1 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-neutral-200 transition-opacity"
        aria-label={`Close ${artifact.title}`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export const ArtifactTabs: React.FC = () => {
  const { openArtifacts, activeArtifact, setActiveArtifact, closeArtifact, createDocument } = useWorkspace();

  const handleNewDocument = async () => {
    try {
      const newArtifact = await createDocument('Untitled Document');
      setActiveArtifact(newArtifact.id);
    } catch (error) {
      console.error('Failed to create new document:', error);
    }
  };

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 bg-neutral-50 border-b border-neutral-200">
      <div className="flex items-center gap-0.5 flex-1 overflow-x-auto">
        {openArtifacts.map((artifact) => (
          <Tab
            key={artifact.id}
            artifact={artifact}
            isActive={activeArtifact?.id === artifact.id}
            onSelect={() => setActiveArtifact(artifact.id)}
            onClose={() => closeArtifact(artifact.id)}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={handleNewDocument}
        className="ml-1 flex items-center justify-center w-7 h-7 rounded text-neutral-500 hover:text-neutral-700 hover:bg-neutral-200 transition-colors shrink-0"
        title="New Document"
        aria-label="Create new document"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
};

export default ArtifactTabs;

